import { useEffect } from 'react';

import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useAuth } from 'react-oidc-context';

import { FeedbackAlert, Loader } from '@/components';
import { routeTree } from '@/routeTree.gen.ts';
import { useConfigStore } from '@/stores';
import { configLoadFailedFeedbackProps } from '@/utils/config.ts';
import { setFrontendLogsUser, trackFrontendLogsPages } from '@/utils/frontendLogs.ts';

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { auth: undefined!, portableMode: undefined },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

trackFrontendLogsPages(router);

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const App = () => {
  const auth = useAuth();
  const { portableMode, clusterName, loaded, error } = useConfigStore();
  // Browser error reports: the B2C id, or local:<cluster> on edge, where users do not log in.
  const reportUserId =
    auth.user?.profile.sub ?? (!portableMode && clusterName ? `local:${clusterName}` : undefined);
  useEffect(() => setFrontendLogsUser(reportUserId), [reportUserId]);

  if (error) {
    return <FeedbackAlert {...configLoadFailedFeedbackProps(error)} />;
  }

  if (!loaded || auth.isLoading) {
    return <Loader />;
  }

  return <RouterProvider router={router} context={{ auth, portableMode }} />;
};
